import { createContext, useContext, useEffect, useRef, useState } from "react";
import { ApiSchema, RequestError, useApi } from "../services";
import { Acomodacao, PolyZas, PolyZss, PontoEncontro, RotaAlternativa, RotaFuga, Sirene, UnidadeSensivel } from "../components";
import { useTheme } from "@mui/material";
import { Cadastro } from "../components/zas/cadastro";
import { Controle } from "../services/api/controle";

export interface Brecha {
  id: number;
  altBrecha: number;
  dataCadastro: Date;
  dataModificacao?: Date;
  elevacaoFundo: number;
  elevacaoTopo: number;
  idModificou: string;
  inclTalude: number;
  larguraBase: number;
  larguraTopo: number;
  naMax: number;
  tempo: number;
  volEscoado: number;
}

export interface GeometryBarragem {
  id: number;
  altMax: number;
  brecha: Brecha;
  compCrista: number;
  cotaCrista: number;
  estruturaVertente: string;
  finalidade: string;
  idBarragem: number;
  instrumentacao: string;
  larguraCrista: number;
  lat: number;
  lon: number;
  metodoAltiamento: string;
  naNormal: number;
  naOperacao: number;
  tipo: string;
  tipoSecao: string;
  volAtual: number;
  volMax: number;
}

export interface Percentages {
  I: number;
  II: number;
  III: number;
  IV: number;
  V: number;
}

export interface BarragemImage {
  id: number;
  url: string;
  fileName: string;
}

export interface ZAS {
  id: number;

  distMaxSirenes?: number;
  distMaxPercorrida?: number;

  lonCenter?: number;
  latCenter?: number;

  images?: string[];

  idModificou?: string;
  dataCadastro: Date;
  dataModificacao?: Date;

  uniSensivel?: UnidadeSensivel[];
  cadastros?: RequestError | Cadastro[];
  rotasFuga?: RequestError | RotaFuga[];
  sirenes?: RequestError | Sirene[];
  pontosEncontro?: RequestError | PontoEncontro[];
  rotasAlternativas?: RequestError | RotaAlternativa[];
  acomodacoes?: RequestError | Acomodacao[];

  polygon?: RequestError | PolyZas;
  polygonZss?: RequestError | PolyZss;
}

export interface BarragemFluxogramas {
  NV0: number | null;
  NV1: number | null;
  NV2: number | null;
  NV3: number | null;
}

export interface BarragemProdutos {
  id: number;
  pae: boolean;
  gistm: boolean;
  manchas: boolean;
  idUpdate?: string;
  dataCadastro: Date;
  dataModificacao?: Date;
}

export interface BarragemData {
  id: number;
  idDocumento: number;
  idEmpreendimento: string;
  idModificou: string;
  idZAS: number;
  idControle: number;
  nome: string;
  descricao: string;
  recMatLogisticos?: string;
  diagnosticoCultural?: string;
  bi_url?: string;
  urlPopZas: string;
  urlRepositorioGISTM?: string;
  dataCadastro: Date;
  dataModificacao?: Date;
  images: BarragemImage[];
  lat: number;
  lon: number;
  percentages: Percentages;
  geometries: GeometryBarragem[];
  zas?: ZAS | RequestError;
  salaControle: Controle | null | RequestError;
  fluxogramas: BarragemFluxogramas | RequestError;
  manchas: number[];
  produtos: BarragemProdutos;
}

interface ContextProps {
  barragem?: BarragemData | RequestError;
  setBarragem: (barragem: BarragemData) => void;
}

const BarragemContext = createContext<ContextProps>({
  setBarragem: () => {
    // 
  },
});

export const useBarragemContext = () => {
  return useContext(BarragemContext);
};


interface Props {
  children?: React.ReactNode;
  barragemId: number;
}
export const BarragemProvider: React.FC<Props> = ({ children, barragemId }) => {
  const theme = useTheme();

  const api = useApi();

  const timer = useRef<NodeJS.Timer>();

  const [_barragemData, _setBarragemData] = useState<BarragemData | RequestError | undefined>(undefined);
  const [barragemData, setBarragemData] = useState<BarragemData | RequestError | undefined>(undefined);
  const [zasData, setZASData] = useState<ZAS | RequestError | undefined>(undefined);
  const [rotasDeFuga, setRotasDeFuga] = useState<RotaFuga[] | RequestError | undefined>(undefined);
  const [rotasAlternativas, setRotasAlternativas] = useState<RotaAlternativa[] | RequestError | undefined>(undefined);
  const [polyZas, setPolyZas] = useState<PolyZas | RequestError | undefined>(undefined);
  const [polyZss, setPolyZss] = useState<PolyZss | RequestError | undefined>(undefined);
  const [pontosEncontro, setPontosEncontro] = useState<PontoEncontro[] | RequestError | undefined>(undefined);
  const [uniSensiveis, setUniSensiveis] = useState<UnidadeSensivel[] | RequestError | undefined>(undefined);
  const [sirenes, setSirenes] = useState<Sirene[] | RequestError | undefined>(undefined);
  const [cadastros, setCadastros] = useState<Cadastro[] | RequestError | undefined>(undefined);
  const [salaControle, setSalaControne] = useState<Controle | null | RequestError | undefined>(undefined);
  const [acomodacoes, setAcomodacoes] = useState<Acomodacao[] | RequestError | undefined>(undefined);
  const [fluxogramas, setFluxogramas] = useState<BarragemFluxogramas | RequestError | undefined>(undefined);

  useEffect(() => {
    // Dados da barragem
    api.barragem.getBarragemById(barragemId).then(_setBarragemData);
  }, []);

  // ZAS Data
  useEffect(() => {
    // Caso não tenha dados da barragem ainda
    if (!_barragemData || !('id' in _barragemData)) return;
    if (_barragemData.zas) return;
    api.zas.getZAS(_barragemData.idZAS).then(setZASData);
  }, [_barragemData]);

  // Dados que dependem da ZAS
  useEffect(() => {
    // Caso não tenha dados da zas
    if (!zasData || !('id' in zasData)) return;
    if (zasData.rotasFuga) return;

    api.zas.getRotasFuga(zasData.id, theme).then(setRotasDeFuga);
    api.zas.getRotasAlternativas(zasData.id, theme).then(setRotasAlternativas);
    api.zas.getPolyZas(zasData.id, theme).then(setPolyZas);
    api.zas.getPolyZss(zasData.id, theme).then(setPolyZss);
    api.zas.getPontosEncontro(zasData.id, theme).then(setPontosEncontro);
    api.zas.getUniSensivel(zasData.id, theme).then(setUniSensiveis);
    api.zas.getSirenes(zasData.id, theme).then(setSirenes);
    api.zas.getCadastros(zasData.id).then(setCadastros);
  }, [zasData]);

  // Dados que dependem do id da Barragem
  useEffect(() => {
    if (!_barragemData || "error" in _barragemData) return;

    api.controle.get(_barragemData.id).then(setSalaControne);
    api.fluxogramas.getAll(_barragemData.id).then(setFluxogramas);
    fetchAllAcomodacoes(api, _barragemData.id).then(setAcomodacoes);
  }, [_barragemData]);

  // Atualize Barragem
  useEffect(() => {
    // Adicionando os dados a Barragem
    if (_barragemData && 'id' in _barragemData) {
      _barragemData.zas = zasData;
    } else {
      return;
    }

    // Adicionando os dados a ZAS
    if (!fluxogramas || !salaControle || !zasData || !rotasDeFuga || !polyZas || !polyZss || !pontosEncontro || !uniSensiveis || !('id' in zasData)) {
      return;
    }

    if (_barragemData) {
      if (timer.current) clearTimeout(timer.current);

      timer.current = setTimeout(() => {

        setBarragemData({
          ..._barragemData,
          salaControle: salaControle ?? null,
          zas: {
            ...zasData,
            rotasFuga: rotasDeFuga,
            polygon: polyZas,
            polygonZss: polyZss,
            pontosEncontro: pontosEncontro,
            uniSensivel: "error" in uniSensiveis ? [] : uniSensiveis,
            sirenes: sirenes,
            cadastros: cadastros,
            rotasAlternativas: rotasAlternativas,
            acomodacoes: acomodacoes,
          },
          fluxogramas: fluxogramas,
        });
      }, 100);
    }
  }, [zasData, polyZas, polyZss, rotasDeFuga, pontosEncontro, uniSensiveis, sirenes, cadastros, rotasAlternativas, salaControle, acomodacoes, fluxogramas]);

  return (
    <BarragemContext.Provider value={{ barragem: barragemData, setBarragem: setBarragemData }}>
      {children}
    </BarragemContext.Provider>
  );
};



const fetchAllAcomodacoes = async (api: ApiSchema, barragemId: number) => {
  let allAcomodacoes: Acomodacao[] = [];
  let page = 1;
  const perPage = 100;

  let totalPages = 1;

  while (page <= totalPages) {
    try {
      const response = await api.acomodacoes.getAll(barragemId, page, perPage);

      if ('items' in response) {
        allAcomodacoes = [...allAcomodacoes, ...response.items];

        totalPages = response.totalPages;

        page += 1;
      } else {
        break;
      }

    } catch (error) {
      break;
    }
  }

  return allAcomodacoes;
};